import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog,
    DialogType,
    DialogFooter,
    PrimaryButton,
    DefaultButton,
    Spinner,
    SpinnerSize,
    MessageBar,
    MessageBarType,
    Stack,
    Text,
    Rating,
    RatingSize,
    TextField,
} from "@fluentui/react";
import { getImageUrls, updateImageInfo, updateImageWithFiles } from "../services/image";

export interface ImageDetailDialogProps {
    isOpen: boolean;
    imageId: string | null;
    imageData: any; // The original image data from the list
    onDismiss: () => void;
    onUpdate?: (updatedData: any) => void; // Callback when data is updated
}

export const ImageDetailDialog: React.FC<ImageDetailDialogProps> = ({
    isOpen,
    imageId,
    imageData,
    onDismiss,
    onUpdate,
}) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [imageUrls, setImageUrls] = useState<string[]>([]);
    const [updating, setUpdating] = useState<boolean>(false);
    const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);

    // Editable state
    const [editedDescription, setEditedDescription] = useState<string>("");
    const [editedTags, setEditedTags] = useState<string>("");
    const [editedRating, setEditedRating] = useState<number | null>(null);

    // Local image modification state
    const [deletedImageIndices, setDeletedImageIndices] = useState<Set<number>>(new Set());
    const [newUploadedFiles, setNewUploadedFiles] = useState<File[]>([]);

    // Check if data has been modified (only relevant in edit mode)
    const hasChanges = isEditMode && imageData && (
        editedDescription !== imageData.description
        || editedTags !== imageData.tags.join(", ")
        || editedRating !== imageData.rating
        || deletedImageIndices.size > 0
        || newUploadedFiles.length > 0
    );

    const fetchImageUrls = useCallback(async () => {
        if (!imageId) return;

        try {
            setLoading(true);
            setError(null);
            const urls = await getImageUrls(imageId);
            setImageUrls(urls);
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error("Failed to fetch image URLs:", err);
            setError("Failed to load image URLs. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [imageId]);

    // Initialize editable data when dialog opens
    useEffect(() => {
        if (isOpen && imageData) {
            setEditedDescription(imageData.description || "");
            setEditedTags(imageData.tags ? imageData.tags.join(", ") : "");
            setEditedRating(imageData.rating);
            setIsEditMode(false); // Reset to view mode when opening
            setDeletedImageIndices(new Set());
            setNewUploadedFiles([]);
        }
    }, [isOpen, imageData]);

    // Fetch image URLs when dialog opens
    useEffect(() => {
        if (isOpen && imageId) {
            fetchImageUrls();
        } else {
            // Reset state when dialog closes
            setImageUrls([]);
            setDeletedImageIndices(new Set());
            setNewUploadedFiles([]);
            setError(null);
            setUpdateSuccess(null);
            setIsEditMode(false);
        }
    }, [isOpen, imageId, fetchImageUrls]);

    // Handle edit mode toggle
    const handleEditToggle = () => {
        if (isEditMode) {
            // Exiting edit mode - reset to original values
            if (imageData) {
                setEditedDescription(imageData.description || "");
                setEditedTags(imageData.tags ? imageData.tags.join(", ") : "");
                setEditedRating(imageData.rating);
                setDeletedImageIndices(new Set());
                setNewUploadedFiles([]);
            }
        }
        setIsEditMode(!isEditMode);
        setError(null);
        setUpdateSuccess(null);
    };

    // Handle reset functionality - reset to original state from server data
    const handleReset = () => {
        if (imageData) {
            setEditedDescription(imageData.description || "");
            setEditedTags(imageData.tags ? imageData.tags.join(", ") : "");
            setEditedRating(imageData.rating);
            setDeletedImageIndices(new Set());
            setNewUploadedFiles([]);
        }
        setError(null);
        setUpdateSuccess(null);
    };

    // Handle local image deletion
    const handleLocalImageDelete = (index: number) => {
        const newDeletedIndices = new Set(deletedImageIndices);
        newDeletedIndices.add(index);
        setDeletedImageIndices(newDeletedIndices);
    };

    // Handle local image upload
    const handleLocalImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { files } = event.target;
        if (files) {
            const newFiles = Array.from(files);
            setNewUploadedFiles((prev) => [...prev, ...newFiles]);
        }
        // Reset input value to allow selecting the same file again
        event.target.value = "";
    };

    // Remove uploaded file before save
    const handleRemoveUploadedFile = (index: number) => {
        setNewUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    };

    // Handle update functionality
    const handleUpdate = async () => {
        if (!imageId || !hasChanges) return;

        try {
            setUpdating(true);
            setError(null);

            // Check if there are image modifications (deleted or new uploaded)
            const hasImageModifications = deletedImageIndices.size > 0 || newUploadedFiles.length > 0;

            if (hasImageModifications) {
                // Use the new updateImageWithFiles function for image modifications
                const updateData = {
                    description: editedDescription.trim(),
                    style: imageData.style || "",
                    tags: editedTags.split(",").map((tag) => tag.trim()).filter((tag) => tag.length > 0),
                    rating: editedRating,
                    newFiles: newUploadedFiles,
                    deletedImageIndices: Array.from(deletedImageIndices),
                    currentImageCount: imageUrls.length,
                    originalFileNames: imageData.fileNames || [],
                };

                const updatedImage = await updateImageWithFiles(imageId, updateData);
                setUpdateSuccess("Image and information updated successfully!");
                setIsEditMode(false); // Exit edit mode after successful update

                // Reset local modification states
                setDeletedImageIndices(new Set());
                setNewUploadedFiles([]);

                // Refresh image URLs to show the updated images
                await fetchImageUrls();

                // Call parent update callback if provided
                if (onUpdate) {
                    onUpdate(updatedImage);
                }
            } else {
                // Only text field updates - use the simpler updateImageInfo
                const updateData = {
                    description: editedDescription.trim(),
                    tags: editedTags.split(",").map((tag) => tag.trim()).filter((tag) => tag.length > 0),
                    rating: editedRating,
                };

                const updatedImage = await updateImageInfo(imageId, updateData);
                setUpdateSuccess("Image information updated successfully!");
                setIsEditMode(false); // Exit edit mode after successful update

                // Call parent update callback if provided
                if (onUpdate) {
                    onUpdate(updatedImage);
                }
            }

            // Auto-close dialog after successful update
            setTimeout(() => {
                onDismiss();
            }, 1500); // Close dialog after 1.5 seconds to allow user to see success message
        } catch (updateError) {
            setError(updateError instanceof Error ? updateError.message : "Update failed");
        } finally {
            setUpdating(false);
        }
    };

    const renderImagePreviews = () => {
        if (loading) {
            return (
                <div style={{ textAlign: "center", padding: "20px" }}>
                    <Spinner size={SpinnerSize.large} label="Loading images..." />
                </div>
            );
        }

        if (error) {
            return (
                <MessageBar
                    messageBarType={MessageBarType.error}
                    onDismiss={() => setError(null)}
                >
                    {error}
                </MessageBar>
            );
        }

        const displayImages = imageUrls.filter((_, index) => !deletedImageIndices.has(index));
        const totalImages = displayImages.length + newUploadedFiles.length;

        return (
            <div style={{ marginTop: "16px" }}>
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "12px",
                }}
                >
                    <Text variant="mediumPlus">
                        Images (
                        {totalImages}
                        ):
                    </Text>
                    {isEditMode && (
                        <div>
                            <input
                                type="file"
                                multiple
                                accept="image/*"
                                style={{ display: "none" }}
                                id="upload-images"
                                onChange={handleLocalImageUpload}
                            />
                            <DefaultButton
                                text="Add Images"
                                iconProps={{ iconName: "Add" }}
                                onClick={() => document.getElementById("upload-images")?.click()}
                                styles={{
                                    root: {
                                        backgroundColor: "#0078d4",
                                        borderColor: "#0078d4",
                                        color: "#ffffff",
                                    },
                                    rootHovered: {
                                        backgroundColor: "#106ebe",
                                        borderColor: "#106ebe",
                                    },
                                }}
                            />
                        </div>
                    )}
                </div>

                {totalImages === 0 ? (
                    <MessageBar messageBarType={MessageBarType.info}>
                        No images found for this ID.
                    </MessageBar>
                ) : (
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 250px))",
                            gap: "16px",
                            height: "100%",
                            overflow: "auto",
                            overflowX: "auto",
                            overflowY: "auto",
                            padding: "16px",
                            border: "1px solid #e1e1e1",
                            borderRadius: "8px",
                            backgroundColor: "#fafafa",
                            gridAutoRows: "min-content",
                            justifyContent: "start",
                        }}
                    >
                        {/* Existing images (not deleted) */}
                        {imageUrls.map((url, index) => {
                            if (deletedImageIndices.has(index)) return null;
                            const fileName = imageData?.fileNames?.[index] || url.split("/").pop() || `Image ${index + 1}`;
                            return (
                                <div key={`${imageId}-${url}`} style={{ textAlign: "center", position: "relative" }}>
                                    {/* Delete button in edit mode */}
                                    {isEditMode && (
                                        <DefaultButton
                                            iconProps={{ iconName: "Delete" }}
                                            onClick={() => handleLocalImageDelete(index)}
                                            styles={{
                                                root: {
                                                    position: "absolute",
                                                    top: "4px",
                                                    right: "4px",
                                                    zIndex: 1,
                                                    minWidth: "32px",
                                                    width: "32px",
                                                    height: "32px",
                                                    backgroundColor: "rgba(255, 255, 255, 0.9)",
                                                    borderColor: "#d13438",
                                                    color: "#d13438",
                                                },
                                                rootHovered: {
                                                    backgroundColor: "#d13438",
                                                    borderColor: "#d13438",
                                                    color: "#ffffff",
                                                },
                                            }}
                                        />
                                    )}
                                    <div
                                        style={{
                                            marginBottom: "8px",
                                            border: "2px solid #e1e1e1",
                                            borderRadius: "6px",
                                            overflow: "hidden",
                                            aspectRatio: "3/2",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "#ffffff",
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                            height: "140px",
                                        }}
                                    >
                                        <img
                                            src={url}
                                            alt={fileName}
                                            style={{
                                                maxWidth: "100%",
                                                maxHeight: "100%",
                                                objectFit: "contain",
                                            }}
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.style.display = "none";
                                                const parent = target.parentElement;
                                                if (parent) {
                                                    parent.innerHTML = `
                                                        <div style="padding: 20px; color: #666; font-size: 14px; text-align: center;">
                                                            Failed to load image
                                                        </div>
                                                    `;
                                                }
                                            }}
                                        />
                                    </div>
                                    <Text variant="medium" style={{ color: "#333", fontWeight: "500" }}>
                                        {fileName}
                                    </Text>
                                </div>
                            );
                        })}

                        {/* Newly uploaded files */}
                        {newUploadedFiles.map((file, index) => {
                            const url = URL.createObjectURL(file);
                            return (
                                <div key={`new-${file.name}-${file.size}-${file.lastModified}`} style={{ textAlign: "center", position: "relative" }}>
                                    {/* Remove button for new files */}
                                    <DefaultButton
                                        iconProps={{ iconName: "Delete" }}
                                        onClick={() => handleRemoveUploadedFile(index)}
                                        styles={{
                                            root: {
                                                position: "absolute",
                                                top: "4px",
                                                right: "4px",
                                                zIndex: 1,
                                                minWidth: "32px",
                                                width: "32px",
                                                height: "32px",
                                                backgroundColor: "rgba(255, 255, 255, 0.9)",
                                                borderColor: "#d13438",
                                                color: "#d13438",
                                            },
                                            rootHovered: {
                                                backgroundColor: "#d13438",
                                                borderColor: "#d13438",
                                                color: "#ffffff",
                                            },
                                        }}
                                    />
                                    <div
                                        style={{
                                            marginBottom: "8px",
                                            border: "2px solid #0078d4",
                                            borderRadius: "6px",
                                            overflow: "hidden",
                                            aspectRatio: "3/2",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            backgroundColor: "#ffffff",
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                            height: "140px",
                                        }}
                                    >
                                        <img
                                            src={url}
                                            alt={file.name}
                                            style={{
                                                maxWidth: "100%",
                                                maxHeight: "100%",
                                                objectFit: "contain",
                                            }}
                                        />
                                    </div>
                                    <Text variant="medium" style={{ color: "#0078d4", fontWeight: "500" }}>
                                        {file.name}
                                        {" "}
                                        (New)
                                    </Text>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Dialog
            hidden={!isOpen}
            onDismiss={updating ? undefined : onDismiss} // Prevent closing during update
            dialogContentProps={{
                type: DialogType.largeHeader,
                title: `Image Details - ${imageId}`,
                subText: "View image information and preview images",
            }}
            modalProps={{
                isBlocking: updating, // Block interaction during update
                styles: {
                    main: {
                        width: "1800px !important",
                        height: "900px !important",
                        maxWidth: "1800px !important",
                        maxHeight: "900px !important",
                        minWidth: "1800px !important",
                        minHeight: "900px !important",
                        overflow: "hidden",
                        position: "relative", // For overlay positioning
                    },
                },
            }}
        >
            {/* Loading overlay during update */}
            {updating && (
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                        backdropFilter: "blur(2px)",
                    }}
                >
                    <Spinner
                        size={SpinnerSize.large}
                        label="Updating image information..."
                        labelPosition="bottom"
                        styles={{
                            root: {
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                            },
                            label: {
                                fontSize: "16px",
                                fontWeight: "500",
                                color: "#323130",
                                marginTop: "16px",
                            },
                        }}
                    />
                    <Text
                        variant="medium"
                        style={{
                            marginTop: "12px",
                            color: "#605e5c",
                            textAlign: "center",
                        }}
                    >
                        Please wait while we save your changes...
                    </Text>
                </div>
            )}
            <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
                {/* Success message */}
                {updateSuccess && (
                    <MessageBar
                        messageBarType={MessageBarType.success}
                        onDismiss={() => setUpdateSuccess(null)}
                        dismissButtonAriaLabel="Close"
                        styles={{ root: { marginBottom: "16px" } }}
                    >
                        {updateSuccess}
                    </MessageBar>
                )}

                {/* Error message */}
                {error && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        onDismiss={() => setError(null)}
                        dismissButtonAriaLabel="Close"
                        styles={{ root: { marginBottom: "16px" } }}
                    >
                        {error}
                    </MessageBar>
                )}

                {imageData && (
                    <>
                        {/* Edit mode toggle and reset buttons */}
                        <div style={{
                            marginBottom: "16px",
                            textAlign: "right",
                            display: "flex",
                            gap: "8px",
                            justifyContent: "flex-end",
                        }}
                        >
                            {isEditMode && (
                                <DefaultButton
                                    text="Reset"
                                    iconProps={{ iconName: "Refresh" }}
                                    onClick={handleReset}
                                    disabled={updating}
                                    styles={{
                                        root: {
                                            backgroundColor: "#f3f2f1",
                                            borderColor: "#8a8886",
                                        },
                                        rootHovered: {
                                            backgroundColor: "#edebe9",
                                            borderColor: "#605e5c",
                                        },
                                    }}
                                />
                            )}
                            <DefaultButton
                                text={isEditMode ? "Cancel Edit" : "Edit"}
                                iconProps={{ iconName: isEditMode ? "Cancel" : "Edit" }}
                                onClick={handleEditToggle}
                                disabled={updating}
                            />
                        </div>

                        {/* Image basic information */}
                        <div style={{ padding: "0 0 24px 0", borderBottom: "1px solid #e1e1e1", marginBottom: "24px" }}>
                            {isEditMode ? (
                                // Edit mode - editable fields
                                <Stack tokens={{ childrenGap: 20 }}>
                                    {/* Description - full width */}
                                    <div>
                                        <TextField
                                            label="Description"
                                            multiline
                                            rows={3}
                                            value={editedDescription}
                                            onChange={(e, value) => setEditedDescription(value || "")}
                                            styles={{
                                                root: { width: "100%" },
                                                fieldGroup: { minHeight: "80px" },
                                            }}
                                        />
                                    </div>

                                    {/* Rating and Tags - horizontal layout */}
                                    <Stack horizontal tokens={{ childrenGap: 20 }} wrap>
                                        <div style={{ flex: 1, minWidth: "200px" }}>
                                            <Text variant="mediumPlus" style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                                                Rating:
                                            </Text>
                                            <Rating
                                                max={5}
                                                rating={editedRating || 0}
                                                size={RatingSize.Large}
                                                allowZeroStars
                                                onChange={(e, rating) => setEditedRating(rating || null)}
                                            />
                                        </div>

                                        <div style={{ flex: 2, minWidth: "300px" }}>
                                            <TextField
                                                label="Tags (comma separated)"
                                                value={editedTags}
                                                onChange={(e, value) => setEditedTags(value || "")}
                                                placeholder="tag1, tag2, tag3..."
                                                styles={{ root: { width: "100%" } }}
                                            />
                                        </div>
                                    </Stack>
                                </Stack>
                            ) : (
                                // View mode - read-only display
                                <Stack horizontal tokens={{ childrenGap: 20 }} wrap>
                                    <div style={{ flex: 2, minWidth: "300px" }}>
                                        <Text variant="mediumPlus" style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                                            Description:
                                        </Text>
                                        <Text variant="medium">{imageData.description}</Text>
                                    </div>

                                    <div style={{ flex: 1, minWidth: "150px" }}>
                                        <Text variant="mediumPlus" style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                                            Rating:
                                        </Text>
                                        {imageData.rating !== null && imageData.rating !== undefined ? (
                                            <Rating
                                                max={5}
                                                rating={imageData.rating}
                                                size={RatingSize.Large}
                                                readOnly
                                                allowZeroStars
                                            />
                                        ) : (
                                            <Text variant="medium" style={{ color: "#a19f9d", fontStyle: "italic" }}>
                                                Not rated
                                            </Text>
                                        )}
                                    </div>

                                    {imageData.tags && imageData.tags.length > 0 && (
                                        <div style={{ flex: 1, minWidth: "200px" }}>
                                            <Text variant="mediumPlus" style={{ fontWeight: "600", marginBottom: "8px", display: "block" }}>
                                                Tags:
                                            </Text>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                                                {imageData.tags.map((tag: string) => (
                                                    <span
                                                        key={`${imageId}-tag-${tag}`}
                                                        style={{
                                                            padding: "6px 12px",
                                                            backgroundColor: "#e1f5fe",
                                                            color: "#0277bd",
                                                            borderRadius: "16px",
                                                            fontSize: "14px",
                                                            fontWeight: "500",
                                                        }}
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </Stack>
                            )}
                        </div>

                        {/* Image previews - takes remaining space */}
                        <div style={{ flex: 1, overflow: "hidden" }}>
                            {renderImagePreviews()}
                        </div>
                    </>
                )}
            </div>

            <DialogFooter>
                {isEditMode && (
                    <PrimaryButton
                        onClick={handleUpdate}
                        text={updating ? "Updating..." : "Update"}
                        iconProps={{ iconName: "Save" }}
                        disabled={updating || !hasChanges}
                    />
                )}
                <DefaultButton
                    onClick={onDismiss}
                    text="Close"
                    disabled={updating}
                />
                {error && !updateSuccess && (
                    <DefaultButton
                        onClick={fetchImageUrls}
                        text="Retry Load Images"
                        iconProps={{ iconName: "Refresh" }}
                        disabled={loading || updating}
                    />
                )}
            </DialogFooter>
        </Dialog>
    );
};
